import { mainnet, testnet } from '@solana/kit'
import { useMemo, useState } from 'react'
import type { ClusterUrl } from '@solana/kit'
import { devnet } from '@solana/kit'
import { createContext } from 'react'

import { SOLANA_CLUSTER } from '@/utils/solanaCluster'

const STORAGE_KEY = 'solana-example-react-app:selected-chain'

const DEFAULT_CHAIN: `solana:${string}` =
  SOLANA_CLUSTER === 'devnet' ? 'solana:devnet' : 'solana:mainnet'

// The public mainnet endpoint rejects browser requests (403), so allow an
// override via env (e.g. an RPC provider URL).
const MAINNET_RPC_URL =
  import.meta.env.VITE_SOLANA_RPC || 'https://api.mainnet-beta.solana.com'
// http(s):// -> ws(s)://, leaving an already-ws(s) URL unchanged.
const MAINNET_WS_URL = MAINNET_RPC_URL.replace(/^http/, 'ws')

const DEVNET_RPC_URL =
  import.meta.env.VITE_SOLANA_DEVNET_RPC || 'https://api.devnet.solana.com'
const DEVNET_WS_URL = DEVNET_RPC_URL.replace(/^http/, 'ws')

export type ChainContext = Readonly<{
  chain: `solana:${string}`
  displayName: string
  setChain?(chain: `solana:${string}`): void
  solanaExplorerClusterName: 'devnet' | 'mainnet-beta' | 'testnet'
  solanaRpcSubscriptionsUrl: ClusterUrl
  solanaRpcUrl: ClusterUrl
}>

export const DEFAULT_CHAIN_CONFIG = Object.freeze({
  chain: 'solana:mainnet',
  displayName: 'Mainnet Beta',
  solanaExplorerClusterName: 'mainnet-beta',
  solanaRpcSubscriptionsUrl: mainnet(MAINNET_WS_URL),
  solanaRpcUrl: mainnet(MAINNET_RPC_URL),
})

export const ChainContext = createContext<ChainContext>(DEFAULT_CHAIN_CONFIG)

export function ChainContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [chain, setChain] = useState(
    () => localStorage.getItem(STORAGE_KEY) ?? DEFAULT_CHAIN
  )

  const contextValue = useMemo<ChainContext>(() => {
    switch (chain) {
      case 'solana:mainnet':
        return {
          chain: 'solana:mainnet',
          displayName: 'Mainnet Beta',
          solanaExplorerClusterName: 'mainnet-beta',
          solanaRpcSubscriptionsUrl: mainnet(MAINNET_WS_URL),
          solanaRpcUrl: mainnet(MAINNET_RPC_URL),
        }
      case 'solana:testnet':
        return {
          chain: 'solana:testnet',
          displayName: 'Testnet',
          solanaExplorerClusterName: 'testnet',
          solanaRpcSubscriptionsUrl: testnet('wss://api.testnet.solana.com'),
          solanaRpcUrl: testnet('https://api.testnet.solana.com'),
        }
      case 'solana:devnet':
      default:
        if (chain !== 'solana:devnet') {
          localStorage.removeItem(STORAGE_KEY)
          console.error(`Unrecognized chain \`${chain}\``)
        }
        return {
          chain: 'solana:devnet',
          displayName: 'Devnet',
          solanaExplorerClusterName: 'devnet',
          solanaRpcSubscriptionsUrl: devnet(DEVNET_WS_URL),
          solanaRpcUrl: devnet(DEVNET_RPC_URL),
        }
    }
  }, [chain])

  return (
    <ChainContext.Provider
      value={useMemo(
        () => ({
          ...contextValue,
          setChain(chain) {
            localStorage.setItem(STORAGE_KEY, chain)
            setChain(chain)
          },
        }),
        [contextValue]
      )}
    >
      {children}
    </ChainContext.Provider>
  )
}
